import React, {useEffect, useRef, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, ResponsiveContext, Anchor } from 'grommet';
import { RootState } from '../../store/reducers';
import { getAdyen } from '../../modules/config';
import {
    getSelectedSavedCard, getSavedCards,
    getShowCardSelectorView, updateShowCardSelectorView,
    getSelectedPaymentMethod, updatePaymentMethodsData, updateSelectedPaymentMethod, isClickToPayEnabled,
    getPaymentMethodsData,
} from '../../modules/billing';
import { AppThunkDispatch } from '../../modules/thunk';
import { translate } from '../../modules/translation';
import AdyenCardPaymentForm from '../AdyenCardPaymentForm/AdyenCardPaymentForm';
import AdyenReturningCardForm from '../AdyenReturningCardForm/AdyenReturningCardForm';
import PaymentCards from '../PaymentCards/PaymentCards';
import { getEmail } from "../../modules/customer";
import AdyenCheckout from "@adyen/adyen-web";
import { getAvailableAdyenMethodsForCart } from "../../services/payments/adyenHelper";
import { payments } from "../../services";
import { PaymentMethodModule } from "../../services/payments/types";
import LoadingIcon from "../LoadingIcon/LoadingIcon";
import '@adyen/adyen-web/dist/adyen.css';
import '../../theme/override.css';
import newRelicData from "../../newrelic";
import { useHistory } from "react-router-dom";

/**
 * Component to be inserted into the list provided by PaymentMethodSelector.
 * Will show a full adyen payment form, a CVV-only form for returning adyen users, or a UI to select between saved cards.
 */
function PaymentMethodSelectorAdyenCardItem() {
    const dispatch: AppThunkDispatch = useDispatch();

    const size = React.useContext(ResponsiveContext);
    const isDesktop = size !== 'small';
    const config = useSelector((state: RootState) => state.config);
    const billing = useSelector((state: RootState) => state.billing);
    const translation = useSelector((state: RootState) => state.translation);
    const customer = useSelector((state: RootState) => state.customer);

    const adyenConfig = getAdyen(config);
    const savedCards = getSavedCards(billing);
    const selectedSavedCard = getSelectedSavedCard(billing);
    const showCardSelector = getShowCardSelectorView(billing);
    const selectedPaymentMethod = getSelectedPaymentMethod(billing);
    const selectedPaymentMethodRef = useRef(selectedPaymentMethod);
    const shopperEmail = getEmail(customer);
    const formattedEmail = shopperEmail.slice(shopperEmail.indexOf('@') - 4);
    const paymentMethodsData = getPaymentMethodsData(billing);
    const history = useHistory();

    const [isClickToPayAvailable, setIsClickToPayAvailable] = useState(false);
    const [ctpError, setCtpError] = useState(false);
    const [showCardForm, setShowCardForm] = useState(false);
    const [stateValid, setStateValid] = useState(false);

    const adyenComponentConfig = {
        adyenCardCheckoutConfig: {
            locale: adyenConfig.locale,
            environment: adyenConfig.environment,
            clientKey: selectedPaymentMethodRef.current?.additionalData?.paymentServiceDetails?.clientKey,
        }
    };

    const hasSavedCards = savedCards.length > 0;

    const strings = {
        useOtherCard: translate(translation, 'useOtherCard', 'Use another card'),
        useSavedCards: translate(translation, 'useSavedCards', 'Use saved cards'),
        Or: translate(translation, 'Or', 'Or'),
        useClickToPay: translate(translation, 'useClickToPay', 'Use Click to Pay'),
    };

    // The adyen forms won't show if adyen card is not the selected payment method.
    const isCardPayment = billing.selectedPaymentMethod.id === 'CARD';
    const ctpContainerRef = React.useRef<HTMLDivElement>(null);
    
    useEffect(() => {
        selectedPaymentMethodRef.current = getSelectedPaymentMethod(billing);
    
    // eslint-disable-next-line    
    }, [stateValid, selectedPaymentMethod]);
    
    // Load Click to Pay
    useEffect(() => {
        if ( !isCardPayment ) {
            dispatch(updatePaymentMethodsData('adyenCard', {
                ...paymentMethodsData.adyenCard,
                adyenCardMode: 'card'
            }))
            return;
        }
        if ( !isClickToPayEnabled(billing) ) {
            setShowCardForm(true)
            return;
        } 
        
        setIsClickToPayAvailable(false);
        // Clear in-memory adyen data upon first loading the form.
        dispatch(updatePaymentMethodsData('adyenCard', {
            adyenData: {},
            isValid: false,
            shouldSaveCard: false,
            adyenCardMode: 'card'
        }));
        let active = true;
        
        ( async() => {
            try {
                const availableMethods = await getAvailableAdyenMethodsForCart();
                const configuration = {
                    ...adyenComponentConfig.adyenCardCheckoutConfig,
                    paymentMethodsResponse: {
                        paymentMethods: availableMethods?.data.paymentMethods.filter(
                            (method: any) => method.type === 'clicktopay'
                        )},
                };
                const checkout = await AdyenCheckout(configuration);
                const clickToPay = checkout.create('clicktopay', {
                    shopperEmail: shopperEmail,
                    onReady() {
                        console.log('Component is ready to interact with the shopper')
                        newRelicData({ actionName: 'PaymentMethodSelectorAdyenCardItem', userFunction: 'onChange', message: `OTP screen displayed for the email id  ${formattedEmail}`});
                    },
                    onChange(state) {
                        const cardSchemeUsed = state.data.paymentMethod.srcScheme;
                        const cardTypeUsed = state.data.paymentMethod.type;
                        newRelicData({ actionName: 'PaymentMethodSelectorAdyenCardItem', userFunction: 'onChange', message: `card used  ${cardSchemeUsed} and ${cardTypeUsed}`});
                    },
                    onError(error) {
                        if(error)
                            newRelicData({ actionName: 'PaymentMethodSelectorAdyenCardItem', userFunction: 'onError', message: error});
                    },
                    onSubmit: async (state: any, element) => {
                        const clickToPayMethod = {
                            ...selectedPaymentMethodRef.current,
                            method: state.data.paymentMethod.type,
                            name: state.data.paymentMethod.type,
                            additionalData: {
                                paymentServiceDetails: { ...selectedPaymentMethodRef.current.additionalData?.paymentServiceDetails, method: state.data.paymentMethod.type }
                            }
                        }
                        dispatch(updateSelectedPaymentMethod(clickToPayMethod));
                        dispatch(updatePaymentMethodsData('adyenCard', {
                            adyenData: state.data,
                            isValid: state.isValid,
                            shouldSaveCard: false,
                            adyenCardMode: 'clickToPay'
                        }));
                        if (state.isValid) {
                            setStateValid(true);
                            setShowCardForm(false);
                            const paymentModule = await payments()?.getPaymentMethodModule(selectedPaymentMethodRef.current.id) as PaymentMethodModule;
                            paymentModule?.setHistory(history);
                            await paymentModule.authorise();
                        }
                    },
                });
                clickToPay.isAvailable().then(() => {
                    setIsClickToPayAvailable(true);
                    setShowCardForm(false);
                    dispatch(updateShowCardSelectorView(false));
                    dispatch(updatePaymentMethodsData('adyenCard', {
                        ...paymentMethodsData.adyenCard,
                        adyenCardMode: 'clickToPay'
                    }));
                    const container = ctpContainerRef.current;
                    if (container) {
                       clickToPay.mount(container); 
                    } else {
                        newRelicData({ actionName: 'PaymentMethodSelectorAdyenCardItem', userFunction: 'isNotAvailable', message: 'unable to mount click to pay'});
                    }
                }).catch((error) => {
                    newRelicData({ actionName: 'PaymentMethodSelectorAdyenCardItem', userFunction: 'catch', message: `CTP not available: ${error?.message || JSON.stringify(error)}`});
                    setIsClickToPayAvailable(false);
                    setShowCardForm(true);
                });
            } catch (error) {
                newRelicData({ actionName: 'PaymentMethodSelectorAdyenCardItem', userFunction: 'catch', message: `Error: ${error?.message || JSON.stringify(error)}`});
                if (active) {
                    setIsClickToPayAvailable(false);
                    setCtpError(true);
                }
            }

        })();
    // eslint-disable-next-line    
    }, [isCardPayment]);


    const showFallbackForms = !isClickToPayAvailable || ctpError || showCardForm;

    return (
        <>
        { isCardPayment  &&   
        <Box>
            { !showCardForm && !isClickToPayAvailable &&
                <Box
                    flex="grow"
                    justify="center"
                    gap="medium"
                    pad= {{'top':'medium'}}
                >
                    <LoadingIcon color="black" size="30px"/>
                </Box>
            }
            <Box style= {isClickToPayAvailable && !showCardForm ? {'display': 'block'} : {'display': 'none'}} pad= {{'top':'medium'}}>
                <div id="clickToPay-container" ref={ctpContainerRef}></div>
            </Box>    
            { showCardSelector && isDesktop && showCardForm
                ?
                    <Box margin={{top: 'medium'}}>
                        <PaymentCards />
                        { isClickToPayAvailable && (
                            <Box direction="column" gap="medium" margin={{'top': 'medium'}} align={"center"}>
                                <Box> {strings.Or} </Box>
                                <Anchor size="medium" onClick={() => {
                                    setShowCardForm(false);
                                    dispatch(updateShowCardSelectorView(false));
                                    dispatch(updateSelectedPaymentMethod({ ...selectedPaymentMethodRef.current, name: 'clicktopay' }));
                                    dispatch(updatePaymentMethodsData('adyenCard', {
                                        ...paymentMethodsData.adyenCard,
                                        adyenCardMode: 'clickToPay'
                                    }))
                                }}>
                                    {strings.useClickToPay}
                                </Anchor>
                            </Box>
                        )}
                    </Box>
                :
                    <>
                        {!showFallbackForms && !stateValid && (
                            <Box direction="column" gap="medium" margin={{'top': 'medium'}} align={"center"}>
                                <Box> {strings.Or} </Box>
                                <Anchor size="medium" onClick={() => {
                                    setShowCardForm(true)
                                    dispatch(updateSelectedPaymentMethod({ ...selectedPaymentMethodRef.current, name: 'CARD' }));
                                    dispatch(updatePaymentMethodsData('adyenCard', {
                                        ...paymentMethodsData.adyenCard,
                                        adyenCardMode: 'card'
                                    }))
                                }}>
                                    {strings.useOtherCard}
                                </Anchor>
                            </Box>
                        )}
                        { showFallbackForms && showCardForm && (
                            <>
                                {selectedSavedCard.name
                                ?
                                    // Form asking for a CVV only
                                    <>
                                        <AdyenReturningCardForm
                                            config={adyenComponentConfig}
                                        />
    
                                        <Box direction="column" gap="medium" margin={{'top': 'medium'}} align={"center"}>
                                            <Anchor size="medium"
                                                    onClick={() => { dispatch(updateShowCardSelectorView(true));
                                                    }}>
                                                {strings.useOtherCard}
                                            </Anchor>
                                        </Box>
                                    </>
                                :
                                    // Form asking for all card data
                                    <>
                                        <AdyenCardPaymentForm
                                            config={adyenComponentConfig}
                                        />
    
                                        <Box direction="column" gap="medium" margin={{'top': 'medium'}} align={"center"}>
                                        <Anchor size="medium"
                                                onClick={() => { dispatch(updateShowCardSelectorView(true));
                                                }}>
                                            { hasSavedCards && strings.useSavedCards }
                                        </Anchor>
                                        </Box>
                                    </>
                                }
                                { isClickToPayAvailable && (
                                    <Box direction="column" gap="medium" margin={{'top': 'medium'}} align={"center"}>
                                        <Box> {strings.Or} </Box>
                                        <Anchor size="medium" onClick={() => {
                                            setShowCardForm(false);
                                            dispatch(updateSelectedPaymentMethod({ ...selectedPaymentMethodRef.current, name: 'clicktopay' }));
                                            dispatch(updatePaymentMethodsData('adyenCard', {
                                                ...paymentMethodsData.adyenCard,
                                                adyenCardMode: 'clickToPay'
                                            }))
                                        }}>
                                            {strings.useClickToPay}
                                        </Anchor>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
            }
        </Box>
        }</>
    );
}

export default PaymentMethodSelectorAdyenCardItem;
